/**
 * Sentry init + event-shaping helpers.
 *
 * Why this lives in its own module:
 *   - `main.tsx` calls `initSentry()` once before React mounts, before any
 *     other code can throw. Keeping the init body here keeps `main.tsx`
 *     readable and lets us tree-shake Sentry out of dev builds entirely
 *     (the `init` function early-returns before importing the SDK in dev).
 *   - The breadcrumb helpers are imported by `api.ts` (one breadcrumb per
 *     worker API call result) and by the online/offline listeners. Putting
 *     them in one place means there's exactly one Sentry-aware seam in the
 *     codebase to audit when we tighten privacy filters later.
 *
 * beta.4 additions:
 *   - `browserTracingIntegration()` is now registered in `integrations`.
 *     This enables automatic fetch instrumentation (Sentry wraps the global
 *     fetch and attaches trace headers) and captures Web Vitals (FCP, LCP,
 *     CLS, FID, TTFB) via the standard PerformanceObserver hooks baked into
 *     the integration — no separate configuration needed.
 *   - `tracesSampleRate` lowered to `0.1` post-beta (2026-05-15)
 *     span is visible in Sentry. Lower to `0.1` before GA.
 *   - `replayIntegration` is intentionally NOT enabled. Replay adds ~30 kB
 *     gzipped and we don't need session video at the booth level. Kept off
 *     per the beta.4 bundle budget constraint.
 *     The span names are `POST <action>` (e.g. `POST saveLead`) which is the
 *     exact filter key wired in Sentry alert rule 2 (slow saveLead p95 > 5s).
 *
 * Privacy posture:
 *   - We never send the passphrase or any masterPassword field — `beforeSend`
 *     scrubs request bodies that contain known secret keys (`passphrase`,
 *     `masterPassword`, `pin`).
 *   - We never send lead PII (name, email, phone) — neither breadcrumbs nor
 *     errors should reference those fields. If we ever add a code path that
 *     could leak them through stack traces, add a `beforeSend` filter here.
 *
 * Free-tier budget:
 *   - 5K events/mo on Sentry's free hosted plan.
 *   - `tracesSampleRate: 0.1` means roughly 1-in-10 transactions are sampled,
 *     Lower to `0.1` before GA to stay well under the 5K cap.
 *   - Estimated steady-state at 0.1: < 50 events/mo.
 */

import * as Sentry from '@sentry/react';
import { ApiError } from './api';
import { androidSentryOptions } from './androidSentry';
import { iosSentryOptions, normalizeIosContextTags, type IosContextTags } from '../../../shared/iosSentry';

const ENV = import.meta.env.MODE;
const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const RELEASE = import.meta.env.VITE_RELEASE_VERSION as string | undefined;
const ANDROID_BUNDLE = import.meta.env.VITE_SENTRY_RUNTIME === 'android-webview';
const IOS_BUNDLE = import.meta.env.VITE_SENTRY_RUNTIME === 'ios-webview';

let _initialized = false;

/**
 * Initialise Sentry. Safe to call multiple times — the second call is a no-op.
 * No-ops in dev (`MODE === 'development'`) so local crashes don't pollute the
 * production project's event stream.
 */
export function initSentry(): void {
	if (_initialized) return;
	if (ENV === 'development') return;
	if (!DSN) {
		// DSN not yet provisioned. Surface a console warning so we can spot it
		// in DevTools, but don't block boot — the app should run fine without
		// Sentry, just without error reporting.
		console.warn('[sentry] VITE_SENTRY_DSN not set; error reporting disabled');
		return;
	}

	if (ANDROID_BUNDLE || IOS_BUNDLE) {
		try {
			Sentry.init({
				...(IOS_BUNDLE
					? iosSentryOptions(DSN, import.meta.env.VITE_SENTRY_NATIVE_RELEASE || '')
					: androidSentryOptions(DSN, import.meta.env.VITE_NATIVE_APP_VERSION || '')),
				// No automatic breadcrumbs, HTTP context, sessions, replay or tracing.
				integrations: [
					Sentry.globalHandlersIntegration(),
					Sentry.browserApiErrorsIntegration(),
					Sentry.linkedErrorsIntegration(),
					Sentry.dedupeIntegration(),
				],
			});
			_initialized = true;
		} catch {
			console.warn('[sentry] Native WebView reporting could not initialize');
		}
		return;
	}

	Sentry.init({
		dsn: DSN,
		release: RELEASE || 'unknown',
		environment: ENV,

		// beta.4: browserTracingIntegration enables:
		//   1. Automatic fetch instrumentation — Sentry wraps window.fetch and
		//      propagates trace headers so back-end spans correlate correctly.
		//   2. Web Vitals capture (FCP, LCP, CLS, FID, TTFB) via the built-in
		//      PerformanceObserver; no extra config required.
		// Replays are kept off — too heavy for booth-level debugging (skipping
		// replayIntegration keeps ~30 kB gzipped off the bundle).
		integrations: [Sentry.browserTracingIntegration()],

		// Do not inject sentry-trace / baggage headers into third-party origins.
		// Keeps Worker request size minimal; same-origin /api/* gets headers
		// via the Worker route mount on scandeckapp.com.
		tracePropagationTargets: [],

		// 10% sample rate post-beta (2026-05-15) so we stay under the 5K
		// free-tier event cap once live events (Philly May 19, NYC May 21,
		// etc.) start generating real traffic. Bump back to 1.0 temporarily
		// when actively investigating a perf regression.
		tracesSampleRate: 0.1,
		// Replays are off — they're heavy and we don't need full session
		// reproduction for booth-level bugs.
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 0,

		beforeSend(event, hint) {
			// 1) Drop noisy ResizeObserver loop errors. These are benign,
			//    fire on legitimate layout work, and would burn through the
			//    free-tier event budget in a single afternoon.
			const msg = (event.message || '') + (hint.originalException as Error)?.message;
			if (typeof msg === 'string' && /ResizeObserver loop/i.test(msg)) {
				return null;
			}

			// 2) Scrub request bodies of known secret fields. Sentry doesn't
			//    auto-capture fetch request bodies, but if we ever add a
			//    breadcrumb that includes one we want this safety net.
			if (event.request?.data && typeof event.request.data === 'object') {
				const data = event.request.data as Record<string, unknown>;
				for (const key of ['passphrase', 'masterPassword', 'pin']) {
					if (key in data) data[key] = '[redacted]';
				}
			}

			return event;
		},

		beforeBreadcrumb(breadcrumb) {
			// Apply the same secret-scrubbing to breadcrumb data — the
			// `rpc.result` breadcrumb in api.ts can include the request
			// envelope, which carries `pin` for vendor login.
			if (breadcrumb.data && typeof breadcrumb.data === 'object') {
				const d = breadcrumb.data as Record<string, unknown>;
				for (const key of ['passphrase', 'masterPassword', 'pin']) {
					if (key in d) d[key] = '[redacted]';
				}
			}
			return breadcrumb;
		},
	});

	// Set the bundle hash from the rendered <script src> as a tag — same
	// source as VersionChip's diagnostic blob, so clicking through from
	// Slack to Sentry to the bundle hash is one trail.
	Sentry.setTag('bundle_hash', readBundleHash());

	wireOnlineOfflineBreadcrumbs();
	_initialized = true;
}

/**
 * Fixed-vocabulary context tags (runbook 9.4 item 6c): which screen, online
 * or not, how deep the offline queue is (bucketed). Values are normalised in
 * shared/iosSentry so nothing outside the allowlist can be set; `null` clears
 * a tag. Set on every runtime (the browser build is demo only now), kept by
 * the iOS filter through its allowlist. No-op before init.
 */
export function setIosContextTags(tags: IosContextTags): void {
	if (!_initialized) return;
	for (const [k, v] of Object.entries(normalizeIosContextTags(tags))) Sentry.setTag(k, v ?? undefined);
}

/** Tag the active vendor session on every subsequent event. Call from session bootstrap. */
export function setVendorContext(vendorId: string | null): void {
	if (!_initialized) return;
	if (ANDROID_BUNDLE || IOS_BUNDLE) return;
	if (vendorId) {
		Sentry.setTag('vendor_id', vendorId);
		Sentry.setUser({ id: vendorId });
	} else {
		Sentry.setTag('vendor_id', null);
		Sentry.setUser(null);
	}
}

/** Capture an arbitrary thrown value with optional context tags. Safe to call
 *  before/without Sentry init — falls back to a single console.error in DEV. */
export function captureException(
	err: unknown,
	context?: { tags?: Record<string, string>; extra?: Record<string, unknown> },
): void {
	if (!_initialized) {
		if (import.meta.env.DEV) console.error('[sentry] (uninitialized)', err, context);
		return;
	}
	Sentry.withScope((scope) => {
		for (const [k, v] of Object.entries(apiErrorTags(err))) scope.setTag(k, v);
		if (context?.tags) for (const [k, v] of Object.entries(context.tags)) scope.setTag(k, v);
		if (context?.extra) for (const [k, v] of Object.entries(context.extra)) scope.setExtra(k, v);
		Sentry.captureException(err);
	});
}

/**
 * 1.1.3: identifier-only tags for a rejected API call so a handled
 * ApiError can be triaged (which call, which status, which worker code).
 * The native iOS filter (shared/iosSentry.ts) allowlists exactly these
 * three names; no lead fields are ever tagged.
 */
export function apiErrorTags(err: unknown): Record<string, string> {
	if (!(err instanceof ApiError)) return {};
	const tags: Record<string, string> = { api_action: err.action };
	if (typeof err.status === 'number') tags.api_status = String(err.status);
	if (typeof err.reason === 'string' && err.reason) tags.api_reason = err.reason;
	return tags;
}

/** Capture a deliberately-thrown test error so support can verify the pipeline. */
export function captureTestError(label: string): string | undefined {
	if (!_initialized) {
		// In dev or with DSN unset, we still want the admin button to give
		// feedback. Returning undefined signals the caller to show a "no DSN
		// configured" toast.
		return undefined;
	}
	return Sentry.captureException(new Error(`scandeck.test_error: ${label}`));
}

// ---- internals ----------------------------------------------------------

function readBundleHash(): string {
	if (typeof document === 'undefined') return 'unknown';
	const scripts = Array.from(document.querySelectorAll('script[src]'));
	for (const s of scripts) {
		const src = s.getAttribute('src') || '';
		const m = src.match(/assets\/index-([a-zA-Z0-9_-]+)\.js/);
		if (m) return m[1];
	}
	return 'unknown';
}

function wireOnlineOfflineBreadcrumbs(): void {
	if (typeof window === 'undefined') return;
	window.addEventListener('online', () => {
		Sentry.addBreadcrumb({ category: 'connectivity', level: 'info', message: 'online' });
	});
	window.addEventListener('offline', () => {
		Sentry.addBreadcrumb({ category: 'connectivity', level: 'warning', message: 'offline' });
	});
}
